import {
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react';
import cn from 'classnames';
import Tour from 'reactour';

// Components
import Globe from '@components/Globe';
import Section from '@components/Section';
import DashboardButton from '@components/DashboardButton';
import Dropdown, { DropdownItemType } from '@components/Dropdown';
import Header from '@components/Header';
import RiskPopup from '@components/RiskPopup';
import RiskLegend from '@components/RiskLegend';
import CountryDetails from '@components/CountryDetails';
import LandscapeNotifier from '@components/LandscapeNotifier';
/*import SwitchButton from '@components/SwitchButton';*/
import Cookies from 'js-cookie'

// API
import * as url from '@api/requests';

// Providers
import { CountriesContext } from '@providers/Countries';
import { LoadingContext } from '@providers/Loading';
import { GlobeContext } from '@providers/Globe';
import { ProductContext } from '@providers/Product';
import { ProfileContext } from '@providers/Profile';

import useViewportChange from '@helpers/useViewportChange';

// Assets
// @ts-ignore
import WhiteshieldLogoWhite from '@assets/whiteshield_logo-white.png';
import WhiteshieldLogo from '@assets/whiteshield_logo.png';
import BackgroundDark from '@assets/login-background.jpg';
import BackgroundLight from '@assets/background-light.jpg';

// Utils
import { countrySortingFunction, getHubAccess, getPreferredHubId } from '@helpers/utils';

import './Dashboard.scss';
import { Switch, styled } from '@mui/material';

const capabilityGroups = [{
    id: "ability",
    title: "Labour Abilities",
}, {
    id: "knowledge",
    title: "Labour Knowledge",
}, {
    id: "skills",
    title: "Labour Skills",
}, {
    id: "workActivity",
    title: "Work Activity",
}, {
    id: "occupationPerc",
    title: "% High Skilled Workers",
}]
const tooltipText = [
    'Ability to absorb external shocks (technogology, social, economic, health)',
    'Abilities are enduring attributes of the individual that influence performance.',
    'Knowledge is organized sets of principles and facts applying in general domains',
    'Skills are developed capacities that facilitate learning or the more rapid acquisition of knowledge, and facilitate performance of activities that occur across jobs.',
    'Work activities are general types of job behaviors occurring on multiple jobs.',
    ' %  of high skilled workers',
    'Green Jobs'
];

const title = process.env.REACT_APP_TITLE || '';

const stepData = [
    {
        selector: 'globe',
        position: 'left',
        data: {
            title: 'Welcome to ' + title + '!',
            content:
                'This tool allows you to assess labour market resilience, sector and occupation demand, and required skills and education. In this demonstration we will show you the key functions of the tool. The default screen shows the sector and occupation heatmap of Saudi Arabia.',
        },
    },
    {
        selector: 'country-selection',
        position: 'right',
        data: {
            title: 'Select a country',
            content:
                'You can select any country here, or by browsing the globe on the right.',
        },
    },
    {
        selector: 'risk-filters',
        position: 'left',
        data: {
            title: 'Labour market insights',
            content: '',
            html: (
                <>
                    <p>
                        This tool allows you to view the resilience, demand and capabilities of labour markets across the globe, including:
                    </p>
                    <ul className="ps-3">
                        <li>Labour market resilience score</li>
                        <li>Labour market demand by sector and occupation</li>
                        <li>Changes in labour market demand</li>
                        <li> Required skills and education</li>
                    </ul>
                </>
            ),
        },
    },
];

const steps = stepData.map(
    ({ selector, position, data: { title, content, html } }) => ({
        selector: `[data-tour="${selector}"]`,
        position,
        content: () => (
            <div>
                <h4 className="border-bottom mb-2 pb-2">{title}</h4>
                <p style={{ fontSize: 12 }}>
                    {content}
                    {html}
                </p>
            </div>
        ),
    }),
);


const dashboardButtonStyles = {
    marginTop: 6,
};

const riskPopupStyles = {
    marginTop: 10,
};

const MaterialUISwitch = styled(Switch)(({ theme }) => ({
    width: 62,
    height: 34,
    padding: 7,
    '& .MuiSwitch-switchBase': {
      margin: 1,
      padding: 0,
      transform: 'translateX(6px)',
      '&.Mui-checked': {
        color: '#fff',
        transform: 'translateX(22px)',
        '& .MuiSwitch-thumb:before': {
          backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
            '#fff',
          )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`,
        },
        '& + .MuiSwitch-track': {
          opacity: 1,
          backgroundColor: theme.palette.mode === 'dark' ? '#8796A5' : '#aab4be',
        },
      },
    },
    '& .MuiSwitch-thumb': {
      backgroundColor: theme.palette.mode === 'dark' ? '#003892' : '#001e3c',
      width: 32,
      height: 32,
      '&::before': {
        content: "''",
        position: 'absolute',
        width: '100%',
        height: '100%',
        left: 0,
        top: 0,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          '#fff',
        )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
      },
    },
    '& .MuiSwitch-track': {
      opacity: 1,
      backgroundColor: theme.palette.mode === 'dark' ? '#8796A5' : '#aab4be',
      borderRadius: 20 / 2,
    },
  }));
const Dashboard = () => {
    const { isMobile } = useViewportChange();
    const { setIsLoading } = useContext(LoadingContext);
    const { countryList, setCountryList, countryDetails, setCountryDetails } = useContext(CountriesContext);
    const { setProductSettings } = useContext(ProductContext);
    const { setSectorsData, setOccupationsData, setProvinceList, setUniversityProgramsData } = useContext(ProfileContext);
    const [sectorsDataForDD, setSectorsDataForDD] = useState<any[]>([]);
    const {
        selectedCountry, setSelectedCountry,
        setGlobePosition,
        globeRisk, setGlobeRisk,
        hoveredCountry, setHoveredCountry,
        setSelectedProvinces
    } = useContext(GlobeContext);
    const [isTourOpen, setIsTourOpen] = useState<boolean>(false);
    const [darkMode, setDarkMode] = useState<boolean>(Cookies.get('darkMode') === "true");
    const [globalFontSize, setGlobalFontSize] = useState<number>(Number(Cookies.get('globalFontSize')) || 14 );
    const [pickedCountry, setPickedCountry] = useState<DropdownItemType | null>(
        null,
    );
    const [selectedDevStageBySector, setSelectedDevStageBySector] = useState<DropdownItemType | null>(
        null,
    );
    const [selectedCapabilityGroups, setSelectedCapabilityGroups] = useState<DropdownItemType | null>(
        null,
    );


    const handleCloseCountryDetails = useCallback(() => {
        setSelectedCountry && setSelectedCountry(null);
        setPickedCountry(null);
        setProvinceList && setProvinceList([])
        setSelectedProvinces && setSelectedProvinces([])
        hoveredCountry && setHoveredCountry && setHoveredCountry(null);
    }, [setSelectedCountry]);// eslint-disable-line

    const handleCountrySelect = (item: any) => {
        setPickedCountry(item);
    }

    useEffect(() => {
        if (!countryList || countryList.length === 0) return;
        const selectCountryDetails = countryList.find(country =>  country.countryCode && country.countryCode === selectedCountry)
        selectCountryDetails && setPickedCountry({
            id: selectCountryDetails.id,
            title: selectCountryDetails.country
        });
    }, [selectedCountry]);// eslint-disable-line



    const handleCloseTour = useCallback(() => {
        setIsTourOpen(false);
        localStorage.setItem('dashboardTour', 'true');
    }, []);

    const dropdownCountryItems = useMemo(() => {
        if (countryList) {
            var hubAccesses = getHubAccess();
            if (!hubAccesses || (hubAccesses && hubAccesses.length === 0))
                return [];

            var tempCountryList = countryList.filter(value => { return hubAccesses.find((h:any) => h.hubId === value.id) });

            return tempCountryList.map(({ id, country }) => ({
                id,
                title: country,
            }));
        }
        return [];
    }, [countryList]);


    useEffect(() => {
        // default selection
        if (!countryList) return
        const defaultSelectedCountry = countryList.find(country => country.id === getPreferredHubId() && country.countryCode)
        defaultSelectedCountry && setPickedCountry({
            id: defaultSelectedCountry.id,
            title: defaultSelectedCountry.country
        });
    }, [countryList]); // eslint-disable-line


    const countrySelectValue = useMemo(() => {
        if (pickedCountry) return pickedCountry.title;
        return 'PICK COUNTRY';
    }, [pickedCountry]);

    const handleSwitchDarkMode = () => {
        document.body.classList.remove(darkMode === true ? "darkMode" : "lightMode")
        document.body.classList.add(darkMode === false ? "darkMode" : "lightMode")
        Cookies.set('darkMode', (!darkMode).toString())
        setDarkMode(!darkMode);
    };

    const handleFontSize = (isIncreasing: boolean) => {
        if (isIncreasing) {
            Cookies.set('globalFontSize', (globalFontSize + 1).toString())
            setGlobalFontSize(globalFontSize + 1)
        } else {
            Cookies.set('globalFontSize', (globalFontSize - 1).toString())
            setGlobalFontSize(globalFontSize - 1)
        }
      }
      useEffect(() => {
        document.documentElement.style.fontSize = globalFontSize+"px"
      },[globalFontSize])

    useEffect(() => {
        async function fetchCountries() {
            setIsLoading(true);

            const [countryErr, countryResponse] = await url.getCountries();
            const [sectorsErr, incomingSectorsData] = await url.getSectorsList();
            const [countryDetailsErr, countryDetailsResponse] = await url.getCountryDetails();
            const [productSettingsErr, productSettingsResponse] = await url.getProductSettings();
            const [occupationErr, incomingOccupationsData] = await url.getOccupationList();
            const [universityProgramsErr, incomingUniversityPrograms] = await url.getUniversityPrograms();

            setIsLoading(false);

            if (countryErr) console.warn(countryErr);
            if (countryDetailsErr) console.warn(countryDetailsErr);
            if (productSettingsErr) console.warn(productSettingsErr);
            if (sectorsErr) console.warn(sectorsErr);
            if (occupationErr) console.warn(occupationErr);
            if (universityProgramsErr) console.warn(universityProgramsErr);

            setCountryList && setCountryList(countryResponse.result.sort(countrySortingFunction));
            setCountryDetails && setCountryDetails(countryDetailsResponse.result);
            setProductSettings && setProductSettings(productSettingsResponse);
            setSectorsData && setSectorsData(incomingSectorsData);
            setOccupationsData && setOccupationsData(incomingOccupationsData);
            setUniversityProgramsData && setUniversityProgramsData(incomingUniversityPrograms);

            setSectorsDataForDD(incomingSectorsData.filter((e: any) => e.show === true).map((e: any) => ({
                id: e.id,
                title: e.serviceName
            })))

            if (localStorage.getItem('dashboardTour') !== 'true') {
                setIsTourOpen(true);
            }
        }

        fetchCountries();
    }, [setCountryList, setIsLoading, setCountryDetails, setProductSettings, setSectorsData, setUniversityProgramsData]); // eslint-disable-line

    useEffect(() => {
        async function fetchProvinces() {
            if (pickedCountry?.id && countryList && countryList.length > 0) {
                const country = countryList.find(({ id }) => id === pickedCountry?.id) || null;
                if (country) {
                    setIsLoading(true);
                    const [provinceErr, provinceResponse] = await url.getLocationsByCountry([country.id]);
                    if (provinceErr) console.warn(provinceErr);
                    setProvinceList && setProvinceList(provinceResponse);
                    setIsLoading(false);
                }
            }
        }

        fetchProvinces();
    }, [countryList, pickedCountry, setIsLoading, setProvinceList]);

    useEffect(() => {
        const country = countryList?.find(({ id }) => id === pickedCountry?.id) || null;
        setGlobePosition && setGlobePosition(country);
    }, [pickedCountry, setGlobePosition, countryList]);

    useLayoutEffect(() => {
        const globeDefaultPosition =
            countryList?.find(({ countryCode }) => countryCode === 'AE') || null;
        setGlobePosition && setGlobePosition(globeDefaultPosition);
    }, [countryList, setGlobePosition]);

    const riskPopupData = useMemo(() => {
        return (
            countryDetails?.find(({ code }: any) => code === hoveredCountry) || null
        );
    }, [countryDetails, hoveredCountry]);

    const globeLegendTitle = useMemo(() => {
        if (globeRisk) {
            if (typeof globeRisk === "string") {
                switch (globeRisk) {
                    case 'glriRank': return 'Rank of Resilience';
                    case 'greenRank': return 'Rank of Green Jobs';
                    case 'ability': return 'Level of Abilities';
                    case 'knowledge': return 'Level of Knowledge';
                    case 'skills': return 'Level of Skills';
                    case 'workActivity': return 'Level of Work Activity';
                    case 'occupationPerc': return 'Level of High Skilled Workers';
                    default: return ''
                }
            } else {
                return `Rank of ${selectedDevStageBySector?.title}`
            }
        }
        return '';
    }, [globeRisk]);// eslint-disable-line

    const sectionStyles = useMemo(() => {
        const width = isMobile ? '50%' : 300;
        const margin = isMobile ? '0 10px 10px' : '0 40px 10px';

        return {
            width,
            margin,
        };
    }, [isMobile]);



    const handleGlobeFilterClick = useCallback(
        ({ currentTarget }) => {
            const { id } = currentTarget.dataset;
            setGlobeRisk && setGlobeRisk(id);
            hoveredCountry && setHoveredCountry && setHoveredCountry(null);
            setSelectedDevStageBySector(null)
            setSelectedCapabilityGroups(null);
        },
        [setGlobeRisk, hoveredCountry, setHoveredCountry],
    );
    const handleDevStageBySectorSelect = async (e: any) => {
        setSelectedDevStageBySector(e);

        const [sectorScoreErr, incomingSectorScore] = await url.getSectorScore(e.id);
        if (sectorScoreErr) console.warn(sectorScoreErr);

        setGlobeRisk && setGlobeRisk({ section: 'sectorScore', data: incomingSectorScore });
        hoveredCountry && setHoveredCountry && setHoveredCountry(null);
        setSelectedCapabilityGroups(null);

    };
    const handleCapabilityGroupsSelect = (e: any) => {
        setSelectedCapabilityGroups(e);
        setGlobeRisk && setGlobeRisk(e.id);
        hoveredCountry && setHoveredCountry && setHoveredCountry(null);
        setSelectedDevStageBySector(null)
    };
    return (
        <div>
            <LandscapeNotifier />
            <Header />
            {!!selectedCountry && (
                <CountryDetails onClose={handleCloseCountryDetails} />
            )}
            <div className="dashboard">
                <div className="dashboard__content">
                    <div
                        className={cn('dashboard__grid-wrapper', {
                            'dashboard__grid-wrapper_active': !!selectedCountry && !isMobile,
                            'dashboard__grid-wrapper_mobile': isMobile,
                        })}
                    >
                        <div className="dashboard__content-col mt-1">
                            <Section
                                title="EXPLORE"
                                tooltip="FIND THE COUNTRY"
                                style={{ ...sectionStyles, width: isMobile ? 200 : 300 }}
                            >
                                <div className="mb-3"><Dropdown
                                    label={countrySelectValue}
                                    data-tour="country-selection"
                                    style={dashboardButtonStyles}
                                    items={dropdownCountryItems}
                                    withFilter
                                    onSelect={handleCountrySelect}
                                /></div>
                                <>
                                    {!isMobile && (
                                        <RiskPopup
                                            style={riskPopupStyles}
                                            visible={!!hoveredCountry && !!riskPopupData}
                                            data={riskPopupData}
                                            isGreenPopup={globeRisk === 'greenRank'}
                                        />
                                    )}
                                </>
                            </Section>
                        </div>
                        <div className="dashboard__globe">
                            {!selectedCountry && <Globe />}
                            {!isMobile && <RiskLegend globeLegendTitle={globeLegendTitle} gradient={selectedDevStageBySector === null} className={globeRisk === 'greenRank' ? "green" : ""} />}
                        </div>
                    </div>
                    <div className="dashboard__content-col mt-2">
                        <Section
                            title="SELECT UNIT"
                            tooltip="CHOOSE UNIT"
                            style={sectionStyles}
                        >
                            <div className="mb-3 text-capitalize"><DashboardButton
                                label="Labour Resilience"
                                data-id="glriRank"
                                tooltip={tooltipText[0]}
                                active={globeRisk === 'glriRank'}
                                onClick={handleGlobeFilterClick}
                            /></div>
                            <div className="mb-3 text-capitalize"><DashboardButton
                                label="Green Jobs"
                                data-id="greenRank"
                                tooltip={tooltipText[6]}
                                active={globeRisk === 'greenRank'}
                                onClick={handleGlobeFilterClick}
                            /></div>
                            <div className="mb-3 text-capitalize">
                                <Dropdown
                                    label={selectedDevStageBySector?.title || "Development Stage by Sector"}
                                    data-tour="capability_groups"
                                    items={sectorsDataForDD}
                                    itemsStyle={{ left: 0, width: "100%" }}
                                    withFilter
                                    onSelect={handleDevStageBySectorSelect}
                                    active={selectedDevStageBySector !== null}
                                /></div>
                            <div className="mb-3 text-capitalize">
                                <Dropdown
                                    label={selectedCapabilityGroups?.title || "O*NET Capability Groups"}
                                    data-tour="capability_groups"
                                    items={capabilityGroups}
                                    itemsStyle={{ left: 0, width: "100%" }}
                                    withFilter
                                    onSelect={handleCapabilityGroupsSelect}
                                    active={selectedCapabilityGroups !== null}
                                /></div>
                        </Section>
                    </div>
                </div>
                <div className="dashboard-footer">
                    <div>Powered by</div>
                    <img src={Cookies.get('darkMode') === "true" ? WhiteshieldLogoWhite : WhiteshieldLogo} alt="" />
                </div>
                    <div
                        className="dashboard__background"
                        style={{ backgroundImage: `url(${darkMode ? BackgroundDark : BackgroundLight})` }}
                    />
                
                <div className="dashboard__mask" />
            </div>
            {!selectedCountry && (
                <div className="dashboard__switch-buttons">
                    <div>
                        {/* <div>Dark mode</div>
                        <SwitchButton
                            labels={['ON', 'OFF']}
                            active={darkMode}  14 100
                            7
                            onClick={handleSwitchDarkMode}
                            width={100}
                        /> */}
                        <div className={`${darkMode ? "bg-dark border-dark" : "bg-white"} border text-center`}>
                <div className="d-flex align-items-center">
                  <button className="btn btn-light btn-sm" onClick={() => handleFontSize(true)}>A+</button>|<button className="btn btn-light btn-sm" onClick={() => handleFontSize(false)}>A-</button>
                  
                </div>
                {Math.round(globalFontSize*100/14)}%
                </div>
                        <MaterialUISwitch sx={{ m: 1 }} onClick={handleSwitchDarkMode} defaultChecked={darkMode} />
                    </div>
                </div>)}
            <Tour
                /* @ts-ignore */
                steps={steps}
                isOpen={!isMobile && isTourOpen}
                onRequestClose={handleCloseTour}
                showNavigationNumber={false}
                showNumber={false}
            />
        </div>
    );
};

export default Dashboard;
